"use client";

import {
  Content as AccordionPrimitiveContent,
  Header as AccordionPrimitiveHeader,
  Item as AccordionPrimitiveItem,
  Trigger as AccordionPrimitiveTrigger,
  Root as AccordionRoot
} from "@radix-ui/react-accordion";
import { cn } from "@watt/ui/utils/classname-tailwind-merge";
import { ChevronDown } from "lucide-react";
import type * as React from "react";

const Accordion = AccordionRoot;

interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitiveItem> {
  ref?: React.Ref<React.ElementRef<typeof AccordionPrimitiveItem>>;
}

function AccordionItem({ className, ref, ...props }: AccordionItemProps) {
  return (
    <AccordionPrimitiveItem
      className={cn("border-b", className)}
      ref={ref}
      {...props}
    />
  );
}
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitiveTrigger> {
  ref?: React.Ref<React.ElementRef<typeof AccordionPrimitiveTrigger>>;
}

function AccordionTrigger({
  className,
  children,
  ref,
  ...props
}: AccordionTriggerProps) {
  return (
    <AccordionPrimitiveHeader className="flex">
      <AccordionPrimitiveTrigger
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 transition-transform duration-200" />
      </AccordionPrimitiveTrigger>
    </AccordionPrimitiveHeader>
  );
}
AccordionTrigger.displayName = AccordionPrimitiveTrigger.displayName;

interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitiveContent> {
  ref?: React.Ref<React.ElementRef<typeof AccordionPrimitiveContent>>;
}

function AccordionContent({
  className,
  children,
  ref,
  ...props
}: AccordionContentProps) {
  return (
    <AccordionPrimitiveContent
      className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      ref={ref}
      {...props}
    >
      <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </AccordionPrimitiveContent>
  );
}
AccordionContent.displayName = AccordionPrimitiveContent.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
